import type { DocumentDto, DocumentValidationReviewDto, DocumentValueDto } from '@app/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Save, Send } from 'lucide-react';
import { useEffect, useState } from 'react';
import { publishDocument } from '../../api/index.js';
import { useCreateDocumentValue, useUpdateDocumentValue } from '../../hooks/useDocuments.js';
import { DocumentRightsButton } from '../DocumentRightsButton.js';
import { DocumentSubmitButton } from '../DocumentSubmitButton.js';
import { InlineDocumentValueValidation } from '../InlineDocumentValueValidation.js';

type SimpleSerialization = { body: string };

function parseBody(s: unknown): string {
  if (!s || typeof s !== 'object') return '';
  const b = (s as SimpleSerialization).body;
  return typeof b === 'string' ? b : '';
}

interface Props {
  document: DocumentDto;
  value: DocumentValueDto | undefined;
  readOnly?: boolean;
  highlightBody?: boolean;
  validationReview?: DocumentValidationReviewDto;
}

export function ToolSimpleRecord({
  document,
  value,
  readOnly = false,
  highlightBody = false,
  validationReview,
}: Props) {
  const qc = useQueryClient();
  const [body, setBody] = useState(() => parseBody(value?.serialization));
  const [hasUnsaved, setHasUnsaved] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [publishStatus, setPublishStatus] = useState<'idle' | 'publishing' | 'ok' | 'error'>(
    'idle',
  );

  const createValue = useCreateDocumentValue(document.id);
  const updateValue = useUpdateDocumentValue(document.id);

  useEffect(() => {
    setBody(parseBody(value?.serialization));
    setHasUnsaved(false);
  }, [value?.serialization]);

  const handleSave = async () => {
    setSaveStatus('saving');
    const serialization: SimpleSerialization = { body };
    try {
      if (value?.id) {
        await updateValue.mutateAsync({ valueId: value.id, input: { serialization } });
      } else {
        await createValue.mutateAsync({ serialization });
      }
      setHasUnsaved(false);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  };

  const handlePublish = async () => {
    if (document.extension !== 'managementReview') return;
    setPublishStatus('publishing');
    try {
      await publishDocument(document.id);
      void qc.invalidateQueries({ queryKey: ['progression', 'site'] });
      setPublishStatus('ok');
      setTimeout(() => setPublishStatus('idle'), 2500);
    } catch {
      setPublishStatus('error');
      setTimeout(() => setPublishStatus('idle'), 3000);
    }
  };

  const valueValidationRow =
    validationReview?.canValidate && value?.id
      ? validationReview.validations.find((v) => v.documentValueId === value.id)
      : undefined;

  return (
    <div>
      <div className="mb-4 flex items-start justify-between rounded-xl border border-slate-200 bg-slate-50 p-4">
        <div>
          <h1 className="text-lg font-bold text-slate-900">{document.name}</h1>
          <p className="mt-0.5 text-xs text-slate-500">
            Créé le {new Date(document.createdAt).toLocaleDateString('fr-FR')}
          </p>
          {hasUnsaved && (
            <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
              <AlertTriangle className="h-3 w-3" />
              Modifications non sauvegardées
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!readOnly ? <DocumentSubmitButton document={document} /> : null}
          <DocumentRightsButton document={document} />
          {document.extension === 'managementReview' && !readOnly && (
            <button
              type="button"
              onClick={() => void handlePublish()}
              disabled={publishStatus === 'publishing'}
              className="flex items-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-4 py-2 text-sm font-medium text-sky-800 hover:bg-sky-100 disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
              {publishStatus === 'publishing'
                ? 'Publication…'
                : publishStatus === 'ok'
                  ? 'Publié'
                  : publishStatus === 'error'
                    ? 'Erreur'
                    : 'Publier la revue'}
            </button>
          )}
          {!readOnly ? (
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saveStatus === 'saving'}
              className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium ${saveStatus === 'saved' ? 'bg-emerald-500 text-white' : saveStatus === 'error' ? 'bg-red-500 text-white' : hasUnsaved ? 'bg-amber-500 text-white hover:bg-amber-600' : 'bg-slate-200 text-slate-600'}`}
            >
              <Save className="h-4 w-4" />
              {saveStatus === 'saving'
                ? 'Sauvegarde…'
                : saveStatus === 'saved'
                  ? 'Sauvegardé !'
                  : saveStatus === 'error'
                    ? 'Erreur'
                    : 'Sauvegarder'}
            </button>
          ) : null}
        </div>
      </div>

      {valueValidationRow && validationReview ? (
        <div className="mb-4">
          <InlineDocumentValueValidation
            documentId={document.id}
            canValidate={validationReview.canValidate}
            validationRow={valueValidationRow}
          />
        </div>
      ) : null}

      <label className="flex flex-col gap-2 text-sm font-medium text-slate-700">
        Contenu
        <textarea
          readOnly={readOnly}
          className={`min-h-[240px] w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-normal outline-none focus:border-emerald-500 ${highlightBody ? 'ring-2 ring-amber-300/90 bg-amber-50/40' : ''}`}
          value={body}
          onChange={(e) => {
            setBody(e.target.value);
            setHasUnsaved(true);
          }}
          placeholder="Saisissez le contenu du document (décisions, indicateurs, actions…)"
        />
      </label>
    </div>
  );
}
